import {
    CloseOutlined,
    CopyOutlined,
    EditOutlined,
    ExclamationCircleFilled,
    ExportOutlined,
    LineChartOutlined,
    MoreOutlined,
    PlusOutlined,
    QrcodeOutlined,
    QuestionCircleFilled,
    RotateLeftOutlined,
    RotateRightOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import {
    Button,
    Card,
    Col,
    Collapse,
    Divider,
    Dropdown,
    Flex,
    Form,
    GetProp,
    Image,
    Input,
    InputNumber,
    List,
    MenuProps,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Tag,
    Tooltip,
    Typography,
    Upload,
    UploadFile,
    UploadProps,
} from 'antd';
import CollapsePanel from 'antd/es/collapse/CollapsePanel';
import {RcFile} from 'antd/es/upload/interface';
import {UploadChangeParam} from 'antd/lib/upload';
import dayjs from 'dayjs';
import React, {useEffect, useReducer, useRef} from 'react';
import {useQueryClient} from 'react-query';
import {useNavigate, useParams} from 'react-router-dom';

import {ApiQuery} from '../../../gen/api';
import {
    AdminCategory,
    AdminImageRotateDirection,
    AdminPalletOrderList,
    AdminProductDetail,
    AdminProductDetailMarketplace,
    AdminProductOrders,
    AdminProductOrdersResp,
} from '../../../gen/api/models';
import {useNotification} from '../../../NotificationContext';
import {useScreenInfo} from '../../../ResponsiveGate';
import {downloadBlobAsFile} from '../../helpers/files';
import {marketplaceLabels, productConsistencies, productStatuses} from '../../helpers/labels';
import {orderPaymentTypeLabel, orderStatusBadge, orderTypeLabel} from '../../helpers/orders';
import {productStatusBadge, productStatusLabel} from '../../helpers/status';
import {capitalize} from '../../helpers/strings';
import {brokenLinkDescription} from './components/BrokenLinkTag';
import {EbayListingCard} from './components/ebay/EbayListingCard';
import {generateProductQR} from './components/GenerateQR';
import {ProductOrderAddStatus} from './components/ProductOrderAddStatus';
import {ProductOrderCreate} from './components/ProductOrderCreate';
import {ProductPriceAnalyticsCard} from './components/ProductPriceAnalyticsCard';
import {useRunPriceAnalytics} from './components/useRunPriceAnalytics';
import styles from './product_detail.module.less';

const {Text} = Typography;
const {confirm} = Modal;

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

interface Order extends AdminProductOrders {
    addStatus: boolean;
}

type OrderAction = {type: 'TOGGLE_EDIT_FLAG'; payload: string} | {type: 'SET_ORDERS'; payload: Order[]};

const orderReducer = (state: Order[], action: OrderAction): Order[] => {
    switch (action.type) {
        case 'TOGGLE_EDIT_FLAG':
            return state.map((product) =>
                product.id === action.payload ? {...product, addStatus: !product.addStatus} : product
            );
        case 'SET_ORDERS':
            return action.payload;
        default:
            return state;
    }
};

const getBase64 = (file: FileType): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

export const ProductDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const {screenMap} = useScreenInfo();
    const {id} = useParams();

    const canvasRef = useRef<HTMLCanvasElement>(null);

    const notify = useNotification();

    const [form] = Form.useForm();

    const [error, setError] = React.useState<string | null>(null);
    const [product, setProduct] = React.useState<AdminProductDetail>();
    const marketplaces = Form.useWatch<Array<AdminProductDetailMarketplace>>('marketplaces', form);
    const asin = Form.useWatch<string>('asin', form);
    const [palletOrder, setPalletOrder] = React.useState<AdminPalletOrderList[]>([]);

    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [previewImage, setPreviewImage] = React.useState('');
    const [fileList, setFileList] = React.useState<UploadFile[]>([]);
    const [orders, dispatchOrders] = useReducer(orderReducer, []);
    const [newOrderModalOpen, setNewOrderModalOpen] = React.useState(false);
    const [expandedOrders, setExpandedOrders] = React.useState<string[]>([]);

    const [categories, setCategories] = React.useState<AdminCategory[]>();

    const categoriesQuery = ApiQuery.useGetAdminProductCategoryListQuery({
        onSuccess(data) {
            setCategories(data.items);
        },
        refetchOnMount: 'always',
    });

    const palletOrderQuery = ApiQuery.useAdminGetAllPalletsQuery({
        onSuccess(data) {
            setPalletOrder(data.items!);
        },
        refetchOnMount: 'always',
    });

    const productConsistencyDetails = ApiQuery.useGetAdminProductsConsistencyDetailsQuery({
        refetchOnMount: 'always',
    });

    const productQuery = ApiQuery.useGetAdminProductDetailQuery(
        {
            id: id!,
        },
        {
            onSuccess(data) {
                form.setFieldsValue({
                    name: data.name,
                    category: data.category.id,
                    pallet_order_id: data.palletOrderID,
                    description: data.description,
                    msrp: data.mSRP,
                    status: data.status,
                    consistency: data.consistency,
                    consistency_details: data.consistencyDetails,
                    marketplaces: data.marketplaces,
                    asin: data.asin,
                    lowest_resale_price: data.lowestResalePrice,
                    purchase_price: data.purchasePrice,
                });
                setProduct(data);
                if (data.images) {
                    setFileList(
                        data.images.map(
                            (image): UploadFile<any> => ({
                                uid: image.id.toString(),
                                name: image.name,
                                status: 'done',
                                url: image.originalPath,
                                thumbUrl: image.thumbnailPath,
                            })
                        )
                    );
                }
                // form.setFieldValue('marketplaces', data.marketplaces);
            },
            refetchOnMount: 'always',
            enabled: !palletOrderQuery.isLoading && !categoriesQuery.isLoading,
        }
    );

    const ordersQuery = ApiQuery.useAdminGetOrdersByProductIDMutation({
        onSuccess: (data) => {
            const updatedOrders = data.items?.map((order) => ({
                ...order,
                addStatus: false,
            }));

            dispatchOrders({type: 'SET_ORDERS', payload: updatedOrders!});
        },
    });

    useEffect(() => {
        const handleKeyDown = (event: {key: string}) => {
            if (event.key === 'Escape') {
                navigate('/products');
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        // Cleanup the event listener on unmount
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        ordersQuery.mutate(
            {id: id!},
            {
                onSuccess(data: AdminProductOrdersResp) {
                    if (data.items !== undefined && data.items.length > 0) {
                        setExpandedOrders([data.items[0].id]);
                    }
                },
            }
        );
    }, []);

    const attachImages = ApiQuery.usePostAdminAttachImagesMutation({
        onSuccess() {
            notify.openNotificationWithIcon('success', 'Images attached');
            productQuery.refetch();
        },
        onError() {
            notify.openNotificationWithIcon('error', 'Failed to attach images');
        },
    });

    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as FileType);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    };

    const deleteImageQuery = ApiQuery.useDeleteAdminDeleteImageMutation({});

    const handleRemoveImage = async (file: UploadFile) => {
        return new Promise((resolve, reject) => {
            deleteImageQuery
                .mutateAsync({
                    productId: product?.id!,
                    imageId: parseInt(file.uid),
                })
                .then(() => {
                    const index = fileList.indexOf(file);
                    const newFileList = fileList.slice();
                    newFileList.splice(index, 1);
                    setFileList(newFileList);
                    notify.openNotificationWithIcon('success', 'Images deleted');
                    resolve(true);
                })
                .catch(() => {
                    notify.openNotificationWithIcon('error', 'Failed to delete images');
                    reject();
                });
        });
    };

    const rotateImageQuery = ApiQuery.usePostAdminRotateImageMutation({});
    const [rotating, setRotating] = React.useState<{uid: string; direction: AdminImageRotateDirection}>();

    const handleRotateImage = (file: UploadFile, direction: AdminImageRotateDirection) => {
        setRotating({uid: file.uid, direction});
        rotateImageQuery
            .mutateAsync({
                id: product!.id,
                imageId: parseInt(file.uid),
                params: {direction},
            })
            .then((image) => {
                // Only the rotated image changes: refetching the product would reset unsaved form edits.
                setFileList((list) =>
                    list.map((f) =>
                        f.uid === file.uid
                            ? {...f, name: image.name, url: image.originalPath, thumbUrl: image.thumbnailPath}
                            : f
                    )
                );
            })
            .catch(() => {
                notify.openNotificationWithIcon('error', 'Failed to rotate image');
            })
            .finally(() => setRotating(undefined));
    };

    const handleAppendImage = (file: RcFile) => {
        attachImages.mutate({
            id: product!.id,
            images: [file],
        });
        return false;
    };

    const OnChange = (info: UploadChangeParam) => {
        console.log('info', info);
        setFileList(info.fileList);
    };

    const uploadButton = (
        <button
            style={{border: 0, background: 'none'}}
            type="button"
        >
            <PlusOutlined />
            <div style={{marginTop: 8}}>Upload</div>
        </button>
    );

    const queryClient = useQueryClient();
    const updateQuery = ApiQuery.usePutAdminProductUpdateMutation({
        onSuccess() {
            notify.openNotificationWithIcon('success', 'Product updated');
            productQuery.refetch();
            // Saving the product as sold, reserved or taken ends its eBay listing.
            queryClient.invalidateQueries(ApiQuery.getAdminEbayListingQueryKey({id: product!.id}));
        },
        onError(errors) {
            const message = errors?.[0]?.message;
            setError(message ? capitalize(message) : 'Something went wrong');
        },
    });

    // Reads the product without productQuery, whose onSuccess would reset the unsaved form edits.
    const freshProductQuery = ApiQuery.useGetAdminProductDetailMutation();
    // The worker published, repriced, relisted or ended the eBay listing: the eBay
    // rows take their saved state, the other rows keep what is being edited.
    const refreshEbayMarketplaces = () => {
        freshProductQuery.mutate(
            {id: id!},
            {
                onSuccess(data) {
                    const fresh = (data.marketplaces ?? []).filter((m) => m.managedByEbay);
                    const edited: AdminProductDetailMarketplace[] = form.getFieldValue('marketplaces') ?? [];
                    const kept = edited.flatMap((m) => {
                        if (!m.managedByEbay) {
                            return [m];
                        }
                        const saved = fresh.find((f) => f.id === m.id);
                        return saved ? [saved] : [];
                    });
                    const added = fresh.filter((f) => !kept.some((m) => m.id === f.id));
                    form.setFieldValue('marketplaces', [...added, ...kept]);
                },
            }
        );
    };

    const handleSubmit = () => {
        setError(null);
        updateQuery.mutate({
            id: product!.id,
            params: {
                categoryID: form.getFieldValue('category'),
                palletOrderID: form.getFieldValue('pallet_order_id'),
                description: form.getFieldValue('description'),
                mSRP: form.getFieldValue('msrp'),
                name: form.getFieldValue('name'),
                status: form.getFieldValue('status'),
                // An eBay sale may have changed the status since the page was loaded.
                loadedStatus: product!.status,
                marketplaces: form.getFieldValue('marketplaces'),
                consistency: form.getFieldValue('consistency'),
                consistencyDetails: form.getFieldValue('consistency_details'),
                asin: form.getFieldValue('asin'),
                lowestResalePrice: form.getFieldValue('lowest_resale_price'),
                purchasePrice: form.getFieldValue('purchase_price'),
            },
        });
    };

    const toggleEditOrderStatusFlag = (productId: string) => {
        dispatchOrders({type: 'TOGGLE_EDIT_FLAG', payload: productId});
    };

    const priceAnalytics = useRunPriceAnalytics();

    const actionMenu: MenuProps['items'] = [
        {
            label: 'Generate QR',
            key: '1',
            icon: <QrcodeOutlined />,
            onClick: () => generateProductQR(canvasRef, product?.name!, product?.listingNumber.toString()!),
        },
        {
            label: 'Duplicate',
            key: '2',
            icon: <CopyOutlined />,
            onClick: () => navigate(`/products/new?clone=${product?.id}`),
        },
        {
            label: 'Run Price Analytics',
            key: '3',
            icon: <LineChartOutlined />,
            disabled: !product?.id || priceAnalytics.isRunning,
            onClick: () => product?.id && priceAnalytics.run([product.id]),
        },
    ];

    const downloadQuery = ApiQuery.useGetAdminProductDownloadImageMutation({
        onSuccess(data) {
            console.log('data', data);
        },
    });

    const handleDownload = (id: string) => {
        downloadQuery
            .mutateAsync({
                id: product!.id,
                imageId: parseInt(id),
            })
            .then((data) => {
                downloadBlobAsFile(data, `${id}.jpg`);
            });
    };

    return (
        <div>
            {newOrderModalOpen && product && (
                <ProductOrderCreate
                    productId={product?.id}
                    marketplaces={marketplaces}
                    onOrderCreated={() => {
                        setNewOrderModalOpen(false);
                        ordersQuery.mutate({id: product.id});
                    }}
                    onClosed={() => setNewOrderModalOpen(false)}
                />
            )}
            {productQuery.isLoading || categoriesQuery.isLoading ? (
                <Spin />
            ) : productQuery.isError || categoriesQuery.isError ? (
                <Text>Not found</Text>
            ) : !product ? (
                <Text>Not found</Text>
            ) : (
                <>
                    <canvas
                        ref={canvasRef}
                        style={{display: 'none'}}
                    ></canvas>
                    <Flex justify={'space-between'}>
                        <h1>
                            {product?.name} / LN: {product?.listingNumber}
                        </h1>
                        <Dropdown
                            menu={{items: actionMenu}}
                            placement={'bottomRight'}
                        >
                            <Button>
                                <Space>
                                    Actions
                                    <MoreOutlined />
                                </Space>
                            </Button>
                        </Dropdown>
                    </Flex>

                    <Row gutter={16}>
                        <Col
                            xs={{flex: '100%'}}
                            lg={{flex: '60%'}}
                            flex={'auto'}
                        >
                            <Form
                                layout="horizontal"
                                labelCol={{span: 5}}
                                wrapperCol={{span: 19, flex: 1}}
                                colon={false}
                                form={form}
                                labelWrap
                            >
                                <Card
                                    title={
                                        <Flex
                                            align={'center'}
                                            justify={'space-between'}
                                        >
                                            <h2>Product Details</h2>
                                            <Tag color={productStatusBadge(product.status)}>
                                                {productStatusLabel(product.status)}
                                            </Tag>
                                        </Flex>
                                    }
                                >
                                    <Form.Item
                                        label="Name"
                                        name="name"
                                        rules={[{required: true, message: 'Name is required'}]}
                                    >
                                        <Input />
                                    </Form.Item>
                                    <Form.Item label={'ASIN'}>
                                        <Space>
                                            <Form.Item
                                                name={'asin'}
                                                noStyle
                                            >
                                                <Input />
                                            </Form.Item>
                                            <Tooltip title={`Find Product: ${asin}`}>
                                                <Button
                                                    disabled={asin === ''}
                                                    size={'small'}
                                                    type="link"
                                                    onClick={() =>
                                                        window.open(`https://www.google.com/search?q=${asin}`, '_blank')
                                                    }
                                                    icon={<ExportOutlined />}
                                                    iconPosition={'end'}
                                                />
                                            </Tooltip>
                                        </Space>
                                    </Form.Item>

                                    <Form.Item
                                        label="Status"
                                        name="status"
                                        rules={[{required: true, message: 'Status is required'}]}
                                    >
                                        <Select
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="select status"
                                            options={productStatuses.map((m) => ({
                                                label: m.label,
                                                value: m.key,
                                            }))}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Consistency"
                                        name="consistency"
                                        rules={[{required: true, message: 'Consistency is required'}]}
                                    >
                                        <Select
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="select consistency"
                                            options={productConsistencies.map((m) => ({
                                                label: m.label,
                                                value: m.key,
                                            }))}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label={
                                            <Flex
                                                align={'center'}
                                                gap={5}
                                            >
                                                <span>Consistency Details</span>
                                                <Tooltip
                                                    placement="right"
                                                    title="Select from list or type and press Enter to add new"
                                                >
                                                    <QuestionCircleFilled />
                                                </Tooltip>
                                            </Flex>
                                        }
                                        name="consistency_details"
                                    >
                                        <Select
                                            mode="tags"
                                            style={{width: '100%'}}
                                            placeholder="Consistency Details"
                                            options={productConsistencyDetails.data?.items?.map((item) => ({
                                                label: item,
                                                value: item,
                                            }))}
                                        />
                                    </Form.Item>
                                    <Form.Item label="Purchase Price / MSRP / Lowest Price">
                                        <Space.Compact>
                                            <Form.Item
                                                noStyle
                                                name="purchase_price"
                                                rules={[{required: true, message: 'Purchase is required'}]}
                                            >
                                                <InputNumber
                                                    prefix="$"
                                                    min={0}
                                                    max={100000}
                                                    defaultValue={0}
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                noStyle
                                                name="msrp"
                                                rules={[{required: true, message: 'MSRP is required'}]}
                                            >
                                                <InputNumber
                                                    prefix="$"
                                                    min={0}
                                                    max={100000}
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                noStyle
                                                name="lowest_resale_price"
                                                rules={[{required: true, message: 'Lowest Resale Price is required'}]}
                                            >
                                                <InputNumber
                                                    prefix="$"
                                                    min={0}
                                                    max={100000}
                                                    defaultValue={0}
                                                />
                                            </Form.Item>
                                        </Space.Compact>
                                    </Form.Item>
                                    <Form.Item
                                        label="Category"
                                        name="category"
                                        rules={[{required: true, message: 'Category is required'}]}
                                    >
                                        <Select
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="select category"
                                            options={categories?.map((category) => ({
                                                label: category.name,
                                                value: category.id,
                                            }))}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Pallet order"
                                        name="pallet_order_id"
                                    >
                                        <Select
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="select pallet order"
                                            allowClear
                                            options={palletOrder?.map((order) => ({
                                                label: `${dayjs(order.orderDate).format('MM/DD/YYYY hh:mm A')} - ${order.name}`,
                                                value: order.id,
                                            }))}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Description"
                                        name="description"
                                        rules={[{required: true, message: 'Description is required'}]}
                                    >
                                        <Input.TextArea rows={10} />
                                    </Form.Item>
                                </Card>
                                <h3>Marketplaces</h3>
                                <Form.List name="marketplaces">
                                    {(fields, {add, remove}) => (
                                        <div style={{display: 'flex', rowGap: 16, flexDirection: 'column'}}>
                                            {fields.map((field) => {
                                                // Published from the admin: the eBay listing card manages it.
                                                const managed = !!marketplaces?.[field.name]?.managedByEbay;
                                                return (
                                                    <Card
                                                        size="small"
                                                        key={field.key}
                                                        title={
                                                            managed ? (
                                                                <Tooltip title="Published on eBay from the admin. Change, end or relist it on the eBay listing card.">
                                                                    <Tag color="blue">Published automatically</Tag>
                                                                </Tooltip>
                                                            ) : undefined
                                                        }
                                                        extra={
                                                            managed ? undefined : (
                                                                <CloseOutlined
                                                                    onClick={() => {
                                                                        confirm({
                                                                            title: 'Are you sure you want to delete this marketplace?',
                                                                            icon: <ExclamationCircleFilled />,
                                                                            content: (
                                                                                <>
                                                                                    <p>
                                                                                        To confirm the deletion, you
                                                                                        will need to update the product
                                                                                        when you are done.
                                                                                    </p>
                                                                                    <p>
                                                                                        Orders associated with this
                                                                                        marketplace will not be deleted.
                                                                                    </p>
                                                                                </>
                                                                            ),

                                                                            okText: 'Yes',
                                                                            okType: 'danger',
                                                                            cancelText: 'No',
                                                                            onOk() {
                                                                                remove(field.name);
                                                                            },
                                                                            onCancel() {
                                                                                console.log('Cancel');
                                                                            },
                                                                        });
                                                                    }}
                                                                />
                                                            )
                                                        }
                                                    >
                                                        <Form.Item
                                                            label="Marketplace"
                                                            name={[field.name, 'name']}
                                                        >
                                                            <Select
                                                                disabled={managed}
                                                                showSearch
                                                                optionFilterProp="label"
                                                                placeholder="select marketplace"
                                                                options={marketplaceLabels.map((m) => ({
                                                                    label: m.label,
                                                                    value: m.key,
                                                                }))}
                                                            />
                                                        </Form.Item>
                                                        <Form.Item
                                                            label="Link"
                                                            name={[field.name, 'link']}
                                                            validateStatus={
                                                                marketplaces?.[field.name]?.linkBrokenAt
                                                                    ? 'error'
                                                                    : undefined
                                                            }
                                                            help={
                                                                marketplaces?.[field.name]?.linkBrokenAt
                                                                    ? brokenLinkDescription(
                                                                          marketplaces[field.name].linkBrokenAt!
                                                                      )
                                                                    : undefined
                                                            }
                                                        >
                                                            <Input disabled={managed} />
                                                        </Form.Item>
                                                        <Form.Item
                                                            label="Price"
                                                            name={[field.name, 'price']}
                                                        >
                                                            <InputNumber
                                                                min={0}
                                                                max={100000}
                                                                disabled={managed}
                                                            />
                                                        </Form.Item>
                                                        {marketplaces?.[field.name]?.priceSyncedAt ? (
                                                            <Tooltip title="The worker found a different price on the marketplace and copied it here. Changing the price by hand removes this note.">
                                                                <Text
                                                                    type="secondary"
                                                                    style={{fontSize: 12}}
                                                                >
                                                                    <SyncOutlined /> Price synced from{' '}
                                                                    {marketplaceLabels.find(
                                                                        (m) => m.key === marketplaces[field.name]?.name
                                                                    )?.label ?? 'the marketplace'}{' '}
                                                                    at{' '}
                                                                    {dayjs(
                                                                        marketplaces[field.name]?.priceSyncedAt
                                                                    ).format('MM/DD/YYYY HH:mm')}
                                                                </Text>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Card>
                                                );
                                            })}

                                            <Button
                                                style={{marginBottom: 16}}
                                                type="dashed"
                                                onClick={() => add()}
                                                block
                                            >
                                                + Add Marketplace
                                            </Button>
                                        </div>
                                    )}
                                </Form.List>

                                {error && <Text type={'danger'}>{error}</Text>}
                            </Form>
                        </Col>
                        <Col
                            xs={{flex: '100%'}}
                            lg={{flex: '40%'}}
                        >
                            <Card
                                title={'Images'}
                                size={'small'}
                                style={{marginBottom: 16}}
                                loading={attachImages.isLoading || deleteImageQuery.isLoading}
                            >
                                <Upload
                                    listType={screenMap.lg ? 'picture-card' : 'picture'}
                                    fileList={fileList}
                                    onPreview={handlePreview}
                                    beforeUpload={handleAppendImage}
                                    onRemove={(file: UploadFile) => {
                                        return new Promise((resolve, reject) => {
                                            confirm({
                                                title: 'Are you sure you want to delete this picture?',
                                                icon: <ExclamationCircleFilled />,
                                                content: (
                                                    <>
                                                        <p>You will not be able to recover it if you proceed.</p>
                                                    </>
                                                ),

                                                okText: 'Delete',
                                                okType: 'danger',
                                                cancelText: 'Cancel',
                                                onOk() {
                                                    handleRemoveImage(file)
                                                        .then(() => resolve(true))
                                                        .catch(() => reject());
                                                },
                                                onCancel() {
                                                    console.log('Cancel');
                                                },
                                            });
                                        });
                                    }}
                                    onChange={OnChange}
                                    id={product.id}
                                    onDownload={(file) => {
                                        console.log('file', file);
                                        handleDownload(file.uid);
                                    }}
                                    multiple={true}
                                    showUploadList={{
                                        showDownloadIcon: true,
                                    }}
                                    itemRender={(originNode, file) => (
                                        <div className={screenMap.lg ? styles.imageCardItem : styles.imageListItem}>
                                            {originNode}
                                            {file.status === 'done' && (
                                                <div className={styles.rotateActions}>
                                                    <Tooltip title={'Rotate counterclockwise'}>
                                                        <Button
                                                            size={'small'}
                                                            icon={<RotateLeftOutlined />}
                                                            loading={
                                                                rotating?.uid === file.uid &&
                                                                rotating.direction ===
                                                                    AdminImageRotateDirection.RotateCounterclockwise
                                                            }
                                                            disabled={rotating !== undefined}
                                                            onClick={() =>
                                                                handleRotateImage(
                                                                    file,
                                                                    AdminImageRotateDirection.RotateCounterclockwise
                                                                )
                                                            }
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title={'Rotate clockwise'}>
                                                        <Button
                                                            size={'small'}
                                                            icon={<RotateRightOutlined />}
                                                            loading={
                                                                rotating?.uid === file.uid &&
                                                                rotating.direction ===
                                                                    AdminImageRotateDirection.RotateClockwise
                                                            }
                                                            disabled={rotating !== undefined}
                                                            onClick={() =>
                                                                handleRotateImage(
                                                                    file,
                                                                    AdminImageRotateDirection.RotateClockwise
                                                                )
                                                            }
                                                        />
                                                    </Tooltip>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                >
                                    {fileList.length >= 8 ? null : uploadButton}
                                </Upload>
                                {previewImage && (
                                    <Image
                                        wrapperStyle={{display: 'none'}}
                                        preview={{
                                            visible: previewOpen,
                                            onVisibleChange: (visible) => setPreviewOpen(visible),
                                            afterOpenChange: (visible) => !visible && setPreviewImage(''),
                                        }}
                                        src={previewImage}
                                    />
                                )}
                            </Card>
                            <ProductPriceAnalyticsCard productId={product.id} />
                            <EbayListingCard
                                productId={product.id}
                                onWorkerDone={refreshEbayMarketplaces}
                            />
                            <Card
                                title={
                                    <Flex
                                        align={'center'}
                                        justify={'space-between'}
                                    >
                                        <h3>Orders</h3>
                                        {orders.length > 1 && (
                                            <Button
                                                type="text"
                                                onClick={() => {
                                                    setExpandedOrders(
                                                        expandedOrders.length > 0 ? [] : orders.map((order) => order.id)
                                                    );
                                                }}
                                            >
                                                {expandedOrders.length ? 'Collapse All' : 'Expand All'}
                                            </Button>
                                        )}
                                    </Flex>
                                }
                                loading={ordersQuery.isLoading}
                                styles={{body: {padding: 16}, cover: {background: 'transparent'}}}
                            >
                                {orders.length === 0 && (
                                    <Flex
                                        justify={'center'}
                                        style={{marginBottom: 16}}
                                    >
                                        <Text type={'secondary'}>No orders</Text>
                                    </Flex>
                                )}
                                {orders.length > 0 && (
                                    <Collapse
                                        size={'small'}
                                        activeKey={expandedOrders}
                                        style={{marginBottom: 16}}
                                        onChange={(keys) => setExpandedOrders(keys)}
                                    >
                                        {orders?.map((order) => {
                                            return (
                                                <CollapsePanel
                                                    header={
                                                        <Flex
                                                            align={'center'}
                                                            justify={'space-between'}
                                                            style={{fontWeight: 'bold'}}
                                                        >
                                                            <Flex align={'center'}>
                                                                <Text
                                                                    style={{
                                                                        marginRight: 14,
                                                                    }}
                                                                >
                                                                    {orderTypeLabel(order.orderType)}
                                                                </Text>
                                                                {order.marketplace && (
                                                                    <Tag>{order.marketplace.name}</Tag>
                                                                )}
                                                                <Tag>
                                                                    ${order.salePrice}{' '}
                                                                    {orderPaymentTypeLabel(order.paymentType)}
                                                                </Tag>
                                                            </Flex>
                                                            <Tag
                                                                color={orderStatusBadge(
                                                                    order.statuses[order.statuses.length - 1]?.status
                                                                )}
                                                            >
                                                                {capitalize(
                                                                    order.statuses[order.statuses.length - 1]?.status
                                                                )}
                                                            </Tag>
                                                        </Flex>
                                                    }
                                                    key={order.id}
                                                    id={order.id}
                                                >
                                                    <div>
                                                        <List
                                                            size="small"
                                                            bordered
                                                            dataSource={order.statuses}
                                                            header={
                                                                <div
                                                                    style={{
                                                                        marginRight: 10,
                                                                        fontSize: 12,
                                                                        fontWeight: 'bold',
                                                                    }}
                                                                >
                                                                    Statuses
                                                                </div>
                                                            }
                                                            footer={
                                                                <>
                                                                    {order.addStatus ? (
                                                                        <ProductOrderAddStatus
                                                                            productId={product.id}
                                                                            orderId={order.id}
                                                                            onSuccess={() => {
                                                                                toggleEditOrderStatusFlag(order.id);
                                                                                ordersQuery.mutate({id: product.id});
                                                                            }}
                                                                            onCancel={() =>
                                                                                toggleEditOrderStatusFlag(order.id)
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <Flex
                                                                            justify={'end'}
                                                                            align={'center'}
                                                                        >
                                                                            <Button
                                                                                type="text"
                                                                                size={'small'}
                                                                                icon={<EditOutlined />}
                                                                                onClick={() =>
                                                                                    toggleEditOrderStatusFlag(order.id)
                                                                                }
                                                                            >
                                                                                Edit Status
                                                                            </Button>
                                                                        </Flex>
                                                                    )}
                                                                </>
                                                            }
                                                            renderItem={(item) => (
                                                                <List.Item>
                                                                    <>
                                                                        {dayjs(item.createdAt).format(
                                                                            'MM/DD/YYYY hh:mm A'
                                                                        )}
                                                                    </>
                                                                    <Tag
                                                                        color={orderStatusBadge(item.status)}
                                                                        style={{
                                                                            width: 70,
                                                                            justifyContent: 'center',
                                                                            display: 'flex',
                                                                        }}
                                                                    >
                                                                        {capitalize(item.status)}
                                                                    </Tag>
                                                                </List.Item>
                                                            )}
                                                        />
                                                    </div>
                                                </CollapsePanel>
                                            );
                                        })}
                                    </Collapse>
                                )}

                                <Button
                                    block
                                    type="primary"
                                    loading={ordersQuery.isLoading}
                                    icon={<PlusOutlined />}
                                    onClick={() => setNewOrderModalOpen(true)}
                                >
                                    Add Order
                                </Button>
                            </Card>
                        </Col>
                    </Row>
                    <Divider />
                    <Flex
                        justify={'end'}
                        align={'center'}
                        gap={16}
                    >
                        <Button
                            type="default"
                            onClick={() => navigate('/products')}
                        >
                            Cancel
                        </Button>
                        <Flex justify={'end'}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={updateQuery.isLoading}
                                onClick={handleSubmit}
                            >
                                Update
                            </Button>
                        </Flex>
                    </Flex>
                </>
            )}
        </div>
    );
};
